import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp,
  People,
  AccountBalanceWallet,
  Assessment,
  Star
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PAnalytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const { default: apiClient } = await import('../../request/http-common');
      
      const response = await apiClient.get(`/api/admin/p2p/analytics?period=${period}`);
      
      if (response.data && response.data.success && response.data.analytics) {
        setAnalytics(response.data.analytics);
      } else {
        // Set default analytics structure to prevent crashes
        setAnalytics({
          overview: {},
          period_stats: {},
          top_partners: [],
          currency_breakdown: [],
          daily_orders: []
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(Number(value) || 0).toFixed(1)}%`;
  };

  if (loading || !analytics) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  const overviewCards = [
    {
      title: 'Total Partners',
      value: analytics.overview.total_partners,
      subtitle: `${analytics.overview.active_partners} active`,
      icon: People,
      color: theme.palette.primary.main,
    },
    {
      title: 'Total Rates',
      value: analytics.overview.total_rates,
      subtitle: `${analytics.overview.active_rates} active`,
      icon: AccountBalanceWallet,
      color: theme.palette.info.main,
    },
    {
      title: 'Total Orders',
      value: analytics.overview.total_orders,
      subtitle: `${analytics.overview.completed_orders} completed`,
      icon: Assessment,
      color: theme.palette.success.main,
    },
    {
      title: 'Total Volume',
      value: formatCurrency(analytics.overview.total_volume),
      subtitle: 'All time',
      icon: TrendingUp,
      color: theme.palette.secondary.main,
    },
  ];

  const periodCards = [
    {
      title: 'New Partners',
      value: analytics.period_stats.new_partners,
      subtitle: `This ${period}`,
      color: theme.palette.primary.main,
    },
    {
      title: 'New Orders',
      value: analytics.period_stats.new_orders,
      subtitle: `This ${period}`,
      color: theme.palette.info.main,
    },
    {
      title: 'Period Volume',
      value: formatCurrency(analytics.period_stats.period_volume),
      subtitle: `This ${period}`,
      color: theme.palette.success.main,
    },
    {
      title: 'Dispute Rate',
      value: formatPercentage(analytics.overview.dispute_rate),
      subtitle: 'Current rate',
      color: analytics.overview.dispute_rate > 5 ? theme.palette.error.main : theme.palette.warning.main,
    },
  ];

  return (
    <Box>
      {/* Period Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          P2P Analytics
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            label="Period"
          >
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="quarter">This Quarter</MenuItem>
            <MenuItem value="year">This Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overview Cards */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        System Overview
      </Typography>
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        {overviewCards.map((card, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: isMobile ? '140px' : '160px',
                  background: `linear-gradient(135deg, ${card.color}20 0%, ${card.color}10 100%)`,
                  border: `1px solid ${card.color}30`,
                }}
              >
                <CardContent sx={{ 
                  p: isMobile ? 1.5 : 2, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between' 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: card.color,
                        width: isMobile ? 32 : 40,
                        height: isMobile ? 32 : 40,
                      }}
                    >
                      <card.icon sx={{ fontSize: isMobile ? 16 : 20 }} />
                    </Avatar>
                  </Box>
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    fontWeight="bold"
                    color={card.color}
                    gutterBottom
                  >
                    {card.value}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Period Stats */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Period Statistics
      </Typography>
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        {periodCards.map((card, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card
              sx={{
                height: isMobile ? '120px' : '140px',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ 
                p: isMobile ? 2 : 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  fontWeight="bold"
                  color={card.color}
                  gutterBottom
                >
                  {card.value}
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "h6"}
                  fontWeight="medium"
                  color="text.primary"
                  gutterBottom
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Top Partners */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Partners by Orders
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Partner</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.top_partners.slice(0, 10).map((partner, index) => (
                      <TableRow key={partner.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                              {partner.partner_name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {partner.partner_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {partner.country}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {partner.orders_count}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Typography variant="body2">
                              {(Number(partner.average_rating) || 0).toFixed(1)}
                            </Typography>
                            <Star sx={{ fontSize: 14, color: 'warning.main' }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Currency Breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Popular Currencies
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Rates</TableCell>
                      <TableCell align="right">Share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.currency_breakdown.map((currency, index) => {
                      const total = analytics.currency_breakdown.reduce((sum, c) => sum + c.count, 0);
                      const percentage = (currency.count / total) * 100;
                      
                      return (
                        <TableRow key={currency.fiat_currency}>
                          <TableCell>
                            <Chip 
                              label={currency.fiat_currency} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {currency.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {(Number(percentage) || 0).toFixed(1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default P2PAnalytics;
